import { useCallback, useState } from "react";
import type { FieldErrors } from "react-hook-form";

import type { InsuranceVerificationFormFields } from "../form/validation.zod";
import { InsuranceVerificationDataService } from "../services/dataTransformationService";
import { FieldConverter } from "../utils/fieldUtils";

export interface UseInsuranceVerificationFormOptions {
  verification:
    | InsuranceVerificationMessage
    | InsuranceVerificationUpdateMessage
    | null;
  onUpdate: (data: InsuranceVerificationUpdateMessage) => Promise<void>;
  onVerify?: (request: VerifyInsuranceRequest) => Promise<void>;
  onValidationChange?: (
    errors: FieldErrors<InsuranceVerificationFormFields>,
    warnings: string[],
  ) => void;
}

export interface InsuranceVerificationFormState {
  isSubmitting: boolean;
  formData: InsuranceVerificationDetailUpdate | null;
  predictedOutcome: string | null;
  formErrors: FieldErrors<InsuranceVerificationFormFields>;
  validationWarnings: string[];
}

export function useInsuranceVerificationForm({
  verification,
  onUpdate,
  onVerify,
  onValidationChange,
}: UseInsuranceVerificationFormOptions) {
  const [state, setState] = useState<InsuranceVerificationFormState>({
    isSubmitting: false,
    formData: null,
    predictedOutcome: null,
    formErrors: {},
    validationWarnings: [],
  });

  const handleFormDataChange = useCallback(
    (data: InsuranceVerificationDetailUpdate) => {
      setState((prev) => ({ ...prev, formData: data }));
    },
    [],
  );

  const handlePredictedOutcomeChange = useCallback((outcome: string) => {
    setState((prev) => ({ ...prev, predictedOutcome: outcome }));
  }, []);

  const handleValidationChange = useCallback(
    (
      errors: FieldErrors<InsuranceVerificationFormFields>,
      warnings: string[],
    ) => {
      setState((prev) => ({
        ...prev,
        formErrors: errors,
        validationWarnings: warnings,
      }));
      onValidationChange?.(errors, warnings);
    },
    [onValidationChange],
  );

  const submitForm = useCallback(
    async (
      formFields: InsuranceVerificationFormFields,
      options: {
        includeFormCompletion?: boolean;
        explicitStatus?: VerificationStatus;
        explicitOutcome?: VerificationOutcome;
      } = {},
    ) => {
      if (!verification) {
        throw new Error("No verification data available");
      }

      try {
        setState((prev) => ({ ...prev, isSubmitting: true }));

        const updatePayload =
          InsuranceVerificationDataService.transformFormToUpdate(
            formFields,
            verification,
            options,
          );

        await onUpdate(updatePayload);
      } finally {
        setState((prev) => ({ ...prev, isSubmitting: false }));
      }
    },
    [verification, onUpdate],
  );

  const submitFormWithCurrentData = useCallback(
    async (
      options: {
        includeFormCompletion?: boolean;
        explicitStatus?: VerificationStatus;
        explicitOutcome?: VerificationOutcome;
        verificationMethod?: string | null;
      } = {},
    ) => {
      if (!verification || !state.formData) {
        throw new Error("No verification data or form data available");
      }

      // Convert current form data to form fields format and merge with base data
      const baseFields =
        InsuranceVerificationDataService.transformForFormDisplay(verification);
      const formFields: InsuranceVerificationFormFields = {
        ...baseFields,
        hasOrthodonticCoverage:
          state.formData.hasOrthodonticCoverage ??
          baseFields.hasOrthodonticCoverage,
        orthoManualOrAutomaticPayments:
          state.formData.orthoManualOrAutomaticPayments ??
          baseFields.orthoManualOrAutomaticPayments,
        terminationDate:
          state.formData.terminationDate ?? baseFields.terminationDate,
        effectiveDate: state.formData.effectiveDate ?? baseFields.effectiveDate,
        orthoPaymentFrequency:
          state.formData.orthoPaymentFrequency ??
          baseFields.orthoPaymentFrequency,
        orthoLifetimeMaxBenefit:
          state.formData.orthoLifetimeMaxBenefit ??
          baseFields.orthoLifetimeMaxBenefit,
        orthoInitialPaymentPercentage:
          state.formData.orthoInitialPaymentPercentage ??
          baseFields.orthoInitialPaymentPercentage,
        orthoAmountUsed:
          state.formData.orthoAmountUsed ?? baseFields.orthoAmountUsed,
        hasOrthoPlanDeductible:
          FieldConverter.booleanToString(
            state.formData.hasOrthoPlanDeductible,
          ) ?? baseFields.hasOrthoPlanDeductible,
        orthoAmountRemaining:
          state.formData.orthoAmountRemaining ??
          baseFields.orthoAmountRemaining,
        orthoDeductibleRemaining:
          state.formData.orthoDeductibleRemaining ??
          baseFields.orthoDeductibleRemaining,
        orthoPercentagePlanPays:
          state.formData.orthoPercentagePlanPays ??
          baseFields.orthoPercentagePlanPays,
        hasOrthoWaitingPeriod:
          FieldConverter.booleanToString(
            state.formData.hasOrthoWaitingPeriod,
          ) ?? baseFields.hasOrthoWaitingPeriod,
        orthoAgeLimit: state.formData.orthoAgeLimit ?? baseFields.orthoAgeLimit,
        orthoDateOfWaitingPeriod:
          state.formData.orthoDateOfWaitingPeriod ??
          baseFields.orthoDateOfWaitingPeriod,
        coordinationOfBenefits:
          state.formData.coordinationOfBenefits ??
          baseFields.coordinationOfBenefits,
        notes: state.formData.notes ?? baseFields.notes,
        preAuthorizationRequired:
          state.formData.preAuthorizationRequired ??
          baseFields.preAuthorizationRequired,
        groupAcceptsAssignmentOfBenefits:
          state.formData.groupAcceptsAssignmentOfBenefits ??
          baseFields.groupAcceptsAssignmentOfBenefits,
        callReferenceNumber:
          state.formData.callReferenceNumber ?? baseFields.callReferenceNumber,
        verificationMethod:
          options.verificationMethod ??
          state.formData.verificationMethod ??
          baseFields.verificationMethod,
      };

      await submitForm(formFields, options);
    },
    [verification, state.formData, submitForm],
  );

  const submitVerify = useCallback(
    async (options: {
      explicitStatus: VerificationStatus;
      explicitOutcome: VerificationOutcome;
      verificationMethod?: string | null;
      callReferenceNumber?: string | null;
    }) => {
      if (!verification) {
        throw new Error("No verification data available");
      }
      if (!onVerify) {
        throw new Error("onVerify callback is not configured");
      }

      try {
        setState((prev) => ({ ...prev, isSubmitting: true }));

        const baseFields =
          InsuranceVerificationDataService.transformForFormDisplay(
            verification,
          );
        // When the user hasn't changed any fields (formData is null), fall back to
        // the verification's existing data so REVIEW confirmations go through cleanly.
        const mergedFields = state.formData
          ? { ...baseFields, ...state.formData }
          : baseFields;
        const request =
          InsuranceVerificationDataService.transformFormToVerifyRequest(
            mergedFields,
            verification.wieldyId,
            options,
          );

        await onVerify(request);
      } finally {
        setState((prev) => ({ ...prev, isSubmitting: false }));
      }
    },
    [verification, state.formData, onVerify],
  );

  const submitSkipAction = useCallback(
    async (skipAction: SkipActionType) => {
      if (!verification) {
        throw new Error("No verification data available");
      }

      try {
        setState((prev) => ({ ...prev, isSubmitting: true }));

        const updatePayload =
          InsuranceVerificationDataService.createSkipPayload(
            verification,
            skipAction,
          );

        await onUpdate(updatePayload);
      } finally {
        setState((prev) => ({ ...prev, isSubmitting: false }));
      }
    },
    [verification, onUpdate],
  );

  const submitCarrierContactOutcome = useCallback(
    async (carrierContact: CarrierContact) => {
      if (!verification) {
        throw new Error("No verification data available");
      }
      if (!onVerify) {
        throw new Error("onVerify callback is not configured");
      }
      if (carrierContact === "CARRIER_AVAILABLE") {
        throw new Error("CARRIER_AVAILABLE is submitted through submitVerify");
      }

      try {
        setState((prev) => ({ ...prev, isSubmitting: true }));

        await onVerify(
          InsuranceVerificationDataService.createCarrierContactVerifyRequest(
            verification,
            carrierContact,
          ),
        );
      } finally {
        setState((prev) => ({ ...prev, isSubmitting: false }));
      }
    },
    [verification, onVerify],
  );

  const getFormDefaultValues = useCallback(
    () =>
      verification
        ? InsuranceVerificationDataService.transformForFormDisplay(verification)
        : {},
    [verification],
  );

  const canSubmit = useCallback(
    () => !state.isSubmitting && state.formData !== null,
    [state.isSubmitting, state.formData],
  );

  const getValidationState = useCallback(
    () => ({
      hasErrors: Object.keys(state.formErrors).length > 0,
      hasWarnings: state.validationWarnings.length > 0,
      errors: state.formErrors,
      warnings: state.validationWarnings,
    }),
    [state.formErrors, state.validationWarnings],
  );

  return {
    isSubmitting: state.isSubmitting,
    formData: state.formData,
    predictedOutcome: state.predictedOutcome,
    formErrors: state.formErrors,
    validationWarnings: state.validationWarnings,

    handleFormDataChange,
    handlePredictedOutcomeChange,
    handleValidationChange,

    submitForm,
    submitFormWithCurrentData,
    submitVerify,
    submitSkipAction,
    submitCarrierContactOutcome,

    getFormDefaultValues,
    canSubmit,
    getValidationState,
  };
}
